import { useCallback, useRef, useState, type RefObject } from 'react';
import { browseForFolderGrpc } from '../../../shared/api/grpc/revitClient';
import {
  runPickExportFlow,
  type ExportFlowStep,
  type RunExportFlowOptions,
} from '../../../shared/api/grpc/pcfExportClient';
import { selectElements } from '../../../shared/api/revit/revitBridge';
import { useRevitApi } from '../../../shared/api/revit/useRevitApi';
import { snapshotCurrentPcfSettingsStoreForDebug } from '../../../shared/api/pipeex/pcfSettingsClient';
import { useActiveDocumentInfoContext } from '../../../shared/hooks/ActiveDocumentProvider';
import { useExportQuotaStore } from '../../../shared/stores/useExportQuotaStore';
import { useVerifiedSetupRevision } from '../../../shared/stores/useUIStore';
import type { OperationProgressModel } from '../../../shared/ui/progress';
import { getPcfGroupingConfigurationError } from '../../../shared/api/pipeex/pcfGroupingConfiguration';
import { mapLocalPcfProgressToModel } from '../lib/exportProgressModel';
import { getQuotaFailureMessage } from '../lib/exportQuota';
import { getExportResultMessage } from '../lib/exportResultMessage';
import { collectPipingSystemModelElementIds } from '../lib/modelSelection';
import {
  runExportValidationPreflight,
  type ExportValidationContextSource,
} from '../lib/exportValidationPreflight';
import type { PipingSystem } from '../types';
import type { UseExportRunLogReturn } from './useExportRunLog';
import type { UseExportStateReturn } from './useExportState';

export interface ExportPathDialogState {
  open: boolean;
  value: string;
  resolve: ((path: string | null) => void) | null;
}

interface UseExportWorkspaceCommandsOptions {
  exportState: UseExportStateReturn;
  selected: Set<string>;
  exportActionsBlocked: boolean;
  groupingParameterName: string;
  openExportSettings: () => void;
  profile?: RunExportFlowOptions['profile'];
  confirmExportWithUnsavedSettings: () => Promise<boolean>;
  notifyExportBlocked: () => void;
  recordExportResult: UseExportRunLogReturn['recordResult'];
  showSuccess: (message: string) => void;
  showWarning: (message: string) => void;
  showError: (message: string) => void;
  announce: (message: string) => void;
  rememberFocusBeforeOverlay: () => void;
  restoreFocusAfterOverlay: (fallback?: HTMLElement | null) => void;
  outputPathFocusFallbackRef: RefObject<HTMLElement | null>;
}

export function useExportWorkspaceCommands(options: UseExportWorkspaceCommandsOptions) {
  const { call } = useRevitApi();
  const activeDocumentInfo = useActiveDocumentInfoContext();
  const verifiedRevision = useVerifiedSetupRevision();
  const validationContextSourceRef = useRef<ExportValidationContextSource>({
    activeProjectKey: activeDocumentInfo.projectKey,
    documentName: activeDocumentInfo.documentName,
    documentPath: activeDocumentInfo.documentPath,
    verifiedRevision,
    settingsSnapshot: { ...snapshotCurrentPcfSettingsStoreForDebug() },
  });
  validationContextSourceRef.current = {
    activeProjectKey: activeDocumentInfo.projectKey,
    documentName: activeDocumentInfo.documentName,
    documentPath: activeDocumentInfo.documentPath,
    verifiedRevision,
    settingsSnapshot: { ...snapshotCurrentPcfSettingsStoreForDebug() },
  };
  const [modelSelectionBusy, setModelSelectionBusy] = useState(false);
  const [pickExportBusy, setPickExportBusy] = useState(false);
  const [validationPreflightBusy, setValidationPreflightBusy] = useState(false);
  const [pickExportStep, setPickExportStep] = useState<ExportFlowStep | null>(null);
  const [pickExportProgress, setPickExportProgress] = useState<OperationProgressModel | null>(null);
  const [pathDialogState, setPathDialogState] = useState<ExportPathDialogState>({
    open: false,
    value: '',
    resolve: null,
  });

  const promptForOutputPath = useCallback(async (
    promptOptions?: { forcePicker?: boolean }
  ): Promise<string | null> => {
    const existingPath = options.exportState.config.outputPath?.trim();
    if (existingPath && !promptOptions?.forcePicker) {
      return existingPath;
    }

    options.rememberFocusBeforeOverlay();
    const result = await browseForFolderGrpc(existingPath ?? '', 'Select PCF export folder');
    if (result.success && result.path?.trim()) {
      const path = result.path.trim();
      options.exportState.setConfig({ outputPath: path });
      options.announce(`Export destination set to ${path}.`);
      options.restoreFocusAfterOverlay(options.outputPathFocusFallbackRef.current);
      return path;
    }
    if (result.canceled) {
      options.restoreFocusAfterOverlay(options.outputPathFocusFallbackRef.current);
      return null;
    }
    return new Promise<string | null>((resolve) => {
      setPathDialogState({ open: true, value: existingPath ?? '', resolve });
    });
  }, [options]);

  const runValidationBeforeExport = useCallback(async (): Promise<void> => {
    setValidationPreflightBusy(true);
    options.announce('Running validation before export.');
    try {
      const result = await runExportValidationPreflight(call, () => ({
        ...validationContextSourceRef.current,
        settingsSnapshot: { ...snapshotCurrentPcfSettingsStoreForDebug() },
      }));
      if (!result.success) {
        const message = `Pre-export validation could not complete: ${result.error || 'Unknown validation error'}. Export will continue.`;
        options.showWarning(message);
        options.announce(message);
        return;
      }

      if (!result.exportReady) {
        const issueLabel = result.blockingIssueCount === 1 ? 'blocking issue' : 'blocking issues';
        const message = `Validation found ${result.blockingIssueCount} ${issueLabel}. Export will continue.`;
        options.showWarning(message);
        options.announce(message);
        return;
      }

      const message = result.warningIssueCount > 0
        ? `Validation passed with ${result.warningIssueCount} warning${result.warningIssueCount === 1 ? '' : 's'}. Continuing export.`
        : 'Validation passed. Continuing export.';
      (result.warningIssueCount > 0 ? options.showWarning : options.showSuccess)(message);
      options.announce(message);
    } catch (error) {
      const detail = error instanceof Error ? error.message : 'Unknown validation error';
      const message = `Pre-export validation could not complete: ${detail}. Export will continue.`;
      options.showWarning(message);
      options.announce(message);
    } finally {
      setValidationPreflightBusy(false);
    }
  }, [call, options]);

  const mayExport = useCallback(async () => {
    if (options.exportActionsBlocked) {
      options.notifyExportBlocked();
      return false;
    }
    if (options.exportState.exporting || pickExportBusy || validationPreflightBusy) {
      return false;
    }
    const groupingError = getPcfGroupingConfigurationError(
      options.exportState.config.groupBy,
      options.groupingParameterName
    );
    if (groupingError) {
      options.showError(groupingError);
      options.announce(groupingError);
      options.openExportSettings();
      return false;
    }
    if (!(await options.confirmExportWithUnsavedSettings())) {
      return false;
    }
    if (options.exportState.config.includeValidation) {
      await runValidationBeforeExport();
    }
    return true;
  }, [options, pickExportBusy, runValidationBeforeExport, validationPreflightBusy]);

  const handleExportSelected = useCallback(async () => {
    if (!(await mayExport())) {
      return;
    }
    const names = Array.from(options.selected);
    if (names.length === 0) {
      return;
    }
    const outputPath = await promptForOutputPath();
    if (outputPath) {
      await options.exportState.exportSystems(names, outputPath);
    }
  }, [mayExport, options, promptForOutputPath]);

  const handleExportAll = useCallback(async () => {
    if (!(await mayExport())) {
      return;
    }
    const outputPath = await promptForOutputPath();
    if (outputPath) {
      await options.exportState.exportAll(outputPath);
    }
  }, [mayExport, options, promptForOutputPath]);

  const handleBrowseOutputPath = useCallback(async () => {
    if (options.exportActionsBlocked) {
      options.notifyExportBlocked();
      return;
    }
    await promptForOutputPath({ forcePicker: true });
  }, [options, promptForOutputPath]);

  const handleSelectSystemsInModel = useCallback(async (systems: PipingSystem[]) => {
    if (modelSelectionBusy) {
      return;
    }
    const elementIds = collectPipingSystemModelElementIds(systems);
    if (systems.length === 0 || elementIds.length === 0) {
      const message = 'Select one or more systems with model elements before selecting them in Revit.';
      options.showError(message);
      options.announce(message);
      return;
    }

    setModelSelectionBusy(true);
    options.announce(`Selecting ${systems.length} system${systems.length === 1 ? '' : 's'} in the Revit model.`);
    try {
      const result = await selectElements(elementIds);
      const message = result.success
        ? result.message || `Selected ${elementIds.length} model element${elementIds.length === 1 ? '' : 's'} from ${systems.length} system${systems.length === 1 ? '' : 's'} in Revit.`
        : result.error || 'Failed to select systems in the Revit model.';
      (result.success ? options.showSuccess : options.showError)(message);
      options.announce(message);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to select systems in the Revit model.';
      options.showError(message);
      options.announce(message);
    } finally {
      setModelSelectionBusy(false);
    }
  }, [modelSelectionBusy, options]);

  const handlePickAndExport = useCallback(async () => {
    if (!(await mayExport())) {
      return;
    }
    const outputPath = await promptForOutputPath();
    if (!outputPath) {
      return;
    }

    setPickExportBusy(true);
    setPickExportStep('license');
    setPickExportProgress(null);
    options.announce('Returning to Revit — pick the elements to export in the model.');
    try {
      const result = await runPickExportFlow({
        groupBy: options.exportState.config.groupBy,
        outputPath,
        profile: options.profile,
        onStep: (step, label) => {
          setPickExportStep(step);
          options.announce(label);
        },
        onProgress: (progress) => {
          setPickExportStep('calculate');
          setPickExportProgress(mapLocalPcfProgressToModel(progress, outputPath, progress.total));
        },
      });
      if (result.cancelled) {
        options.announce('Model selection canceled — nothing exported.');
        return;
      }
      options.recordExportResult(result, { mode: 'model', outputPath });
      const quotaStore = useExportQuotaStore.getState();
      if (result.quotaExhausted) {
        quotaStore.applyExhausted(result.isDemo, result.remainingExportElements);
      } else {
        quotaStore.applyRunQuota(result.remainingExportElements, result.isDemo);
      }
      if (result.success) {
        const outcome = getExportResultMessage({
          ...result,
          outputPath: result.outputPath ?? outputPath,
          elementsExported: result.elementsPicked,
          failedStep: result.step,
        });
        const message = outcome.message;
        (outcome.kind === 'success' ? options.showSuccess : outcome.kind === 'warning' ? options.showWarning : options.showError)(message);
        options.announce(message);
        return;
      }
      const outcome = getExportResultMessage({
        ...result,
        outputPath: result.outputPath ?? outputPath,
        elementsExported: result.elementsPicked,
        failedStep: result.step,
      });
      const message = outcome.hasOutput
        ? outcome.message
        : result.quotaExhausted || result.quotaInsufficient
          ? getQuotaFailureMessage(
            result.isDemo ?? quotaStore.isDemo,
            result.remainingExportElements,
          )
          : outcome.message;
      (outcome.hasOutput ? options.showWarning : options.showError)(message);
      options.announce(message);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Pick-and-export failed.';
      options.recordExportResult({ success: false, error: message }, { mode: 'model', outputPath });
      options.showError(message);
      options.announce(message);
    } finally {
      setPickExportBusy(false);
      setPickExportStep(null);
      setPickExportProgress(null);
    }
  }, [mayExport, options, promptForOutputPath]);

  const handleClosePathDialog = useCallback(() => {
    pathDialogState.resolve?.(null);
    setPathDialogState({ open: false, value: '', resolve: null });
    options.restoreFocusAfterOverlay(options.outputPathFocusFallbackRef.current);
  }, [options, pathDialogState]);

  const handleConfirmPathDialog = useCallback(() => {
    const path = pathDialogState.value.trim();
    if (!path) {
      return;
    }
    options.exportState.setConfig({ outputPath: path });
    options.announce(`Export destination set to ${path}.`);
    pathDialogState.resolve?.(path);
    setPathDialogState({ open: false, value: '', resolve: null });
    options.restoreFocusAfterOverlay(options.outputPathFocusFallbackRef.current);
  }, [options, pathDialogState]);

  return {
    modelSelectionBusy,
    pickExportBusy,
    validationPreflightBusy,
    pickExportStep,
    pickExportProgress,
    pathDialogState,
    setPathDialogState,
    handleExportSelected,
    handleExportAll,
    handleBrowseOutputPath,
    handleSelectSystemsInModel,
    handlePickAndExport,
    handleClosePathDialog,
    handleConfirmPathDialog,
  };
}
